import { Building2, Check, Circle, Copy, Download, Eye, EyeOff, KeyRound, LockKeyhole, Smartphone, UserRound } from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';
import { ApiError, type MfaSetup } from '@despachador/api-client';
import { authErrorText, isAuthErrorCode } from '@despachador/contracts/auth-errors';
import { PASSWORD_RULES } from '@despachador/domain';
import { errorText, Field, Notice, Spinner, useToast } from '../index';
import { AuthMotif } from './AuthMotif';

/**
 * Small pieces of the sign-in and security screens (docs/authentication.md). The API is the
 * authority on every rule; these only guide. Secrets (codes, setup keys) are shown once, never
 * stored in the browser and leave the screen when the step ends.
 */

/** Code of an API error, or null. */
export const errorCode = (err: unknown): string | null => (err instanceof ApiError ? err.code : null);

/**
 * The sentence for an authentication error: the catalog's for its codes (never says whether an
 * account exists), the generic text for the rest (network, server, validation).
 */
export function authMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (isAuthErrorCode(err.code)) return authErrorText(err.code);
    if (err.status === 429) return authErrorText('RATE_LIMITED');
    if (err.status === 503) return authErrorText('AUTH_UNAVAILABLE');
  }
  return errorText(err);
}

/**
 * 6-digit code, or a recovery code when `recovery`. By default it asks for the code of the
 * authenticator app; `label` and `hint` name another source (the code sent by WhatsApp).
 */
export function CodeInput({
  value,
  onChange,
  recovery,
  autoFocus,
  label = 'Código de verificación',
  hint = 'Los 6 dígitos que muestra ahora tu app de autenticación.',
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  recovery?: boolean;
  /** Focuses the field when it becomes true (and when switching to a recovery code). */
  autoFocus?: boolean;
  label?: string;
  hint?: string;
  /** Why this code was refused, under the field. */
  error?: string | null;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const split = useContext(AuthLayoutContext) === 'split';
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus, recovery]);
  return recovery ? (
    <Field label="Código de recuperación" hint="Uno de los códigos que guardaste al activar la verificación en 2 pasos. Cada uno sirve una sola vez." error={error} start={split ? <KeyRound size={16} /> : undefined}>
      <input ref={ref} className="input mono" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={32} required />
    </Field>
  ) : (
    <Field label={label} hint={hint} error={error} under={split ? <CodeCells value={value} focused={focused} /> : undefined}>
      <input
        ref={ref}
        // split: a transparent field over six cells (paste, SMS autofill and screen readers see one input)
        className={split ? 'input mono code-cells' : 'input mono'}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        // the caret always sits after the last digit, so the active cell is the next one to fill
        onSelect={split ? (e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length) : undefined}
        required
      />
    </Field>
  );
}

/** The six cells drawn under the code field: one digit each, the next to fill marked while focused. */
function CodeCells({ value, focused }: { value: string; focused: boolean }) {
  return (
    <>
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const active = focused && i === Math.min(value.length, 5);
        return (
          <span key={i} className={`code-cell ${value[i] ? 'filled' : ''} ${active ? 'active' : ''} ${active && !value[i] ? 'caret' : ''}`}>
            {value[i] ?? ''}
          </span>
        );
      })}
    </>
  );
}

/** Seconds as "m:ss" (countdowns). */
export const countdown = (seconds: number) => `${Math.floor(Math.max(0, seconds) / 60)}:${String(Math.max(0, seconds) % 60).padStart(2, '0')}`;

/** "2 minutos", "1 hora": how long to wait, rounded up, for a sentence. */
export function waitText(seconds: number): string {
  if (seconds < 90) return 'un minuto';
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} minutos`;
  const h = Math.ceil(seconds / 3600);
  return h === 1 ? '1 hora' : `${h} horas`;
}

/** Seconds a 429 asks to wait (`details.retryAfterS`), or null. */
export function retryAfterSeconds(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 429) return null;
  const s = (err.details as { retryAfterS?: unknown } | undefined)?.retryAfterS;
  return typeof s === 'number' && s > 0 ? s : null;
}

/**
 * A new password: the rule the API enforces shown while typing (met or not, with an icon and text,
 * never only a color), what to avoid, and the repetition that catches a typo before it locks the
 * person out. The API is the authority: its refusal (`WEAK_PASSWORD`) is shown under the field.
 */
export function NewPassword({
  value,
  onChange,
  repeat,
  onRepeat,
  error,
  repeatError,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  repeat: string;
  onRepeat: (v: string) => void;
  /** Why the password was refused (client rule or the API's answer). */
  error?: string | null;
  repeatError?: string | null;
  autoFocus?: boolean;
}) {
  const [shown, setShown] = useState(false);
  const split = useContext(AuthLayoutContext) === 'split';
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  const longEnough = [...value].length >= PASSWORD_RULES.minLength;
  return (
    <>
      <Field
        label="Contraseña nueva"
        error={error}
        start={split ? <LockKeyhole size={16} /> : undefined}
        end={split ? <PasswordToggle shown={shown} onToggle={setShown} /> : undefined}
        hint={
          <span className="password-rules">
            <span className={`password-rule ${longEnough ? 'met' : ''}`}>
              {longEnough ? <Check size={14} aria-hidden /> : <Circle size={14} aria-hidden />}
              Al menos {PASSWORD_RULES.minLength} caracteres
              <span className="sr-only">{longEnough ? ' (cumplido)' : ' (falta)'}</span>
            </span>
            <span className="password-rule">Una frase de varias palabras funciona bien. Evita tu nombre, el de la empresa y claves comunes.</span>
          </span>
        }
      >
        <input
          ref={ref}
          className="input"
          type={shown ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={PASSWORD_RULES.maxLength}
          placeholder={split ? 'Una frase que recuerdes' : undefined}
          required
        />
      </Field>
      <Field label="Repite la contraseña" error={repeatError} start={split ? <LockKeyhole size={16} /> : undefined}>
        <input className="input" type={shown ? 'text' : 'password'} value={repeat} onChange={(e) => onRepeat(e.target.value)} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={PASSWORD_RULES.maxLength} placeholder={split ? 'La misma contraseña' : undefined} required />
      </Field>
      {!split && (
        <label className="check small">
          <input type="checkbox" checked={shown} onChange={(e) => setShown(e.target.checked)} /> Mostrar la contraseña
        </label>
      )}
    </>
  );
}

/**
 * The eye inside a password field (split layout): a toggle with a fixed name whose pressed state
 * says whether the password is visible. It never takes the focus away from the form's order.
 */
export function PasswordToggle({ shown, onToggle }: { shown: boolean; onToggle: (shown: boolean) => void }) {
  return (
    <button type="button" className="input-affix-btn" aria-label="Mostrar la contraseña" aria-pressed={shown} title={shown ? 'Ocultar la contraseña' : 'Mostrar la contraseña'} onClick={() => onToggle(!shown)}>
      {shown ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
    </button>
  );
}

/** «Celular o correo» of the sign-in and the recovery; the split layout shows an example inside. */
export function IdentifierField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const split = useContext(AuthLayoutContext) === 'split';
  return (
    <Field label="Celular o correo" hint={split ? undefined : 'Tu número de celular (por ejemplo 300 123 4567) o tu correo.'} start={split ? <UserRound size={16} /> : undefined}>
      <input
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        maxLength={320}
        placeholder={split ? '300 123 4567 o nombre@correo.com' : undefined}
        required
      />
    </Field>
  );
}

/** «Empresa», when the address does not identify the company (development). Lowercase, no spaces. */
export function TenantField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const split = useContext(AuthLayoutContext) === 'split';
  return (
    <Field label="Empresa" hint={split ? 'El identificador que te entregó tu empresa.' : 'Identificador que te entregó tu empresa, por ejemplo: rapitaxi'} start={split ? <Building2 size={16} /> : undefined}>
      <input
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value.trim().toLowerCase())}
        required
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoComplete="organization"
        placeholder={split ? 'rapitaxi' : undefined}
      />
    </Field>
  );
}

/** QR code drawn as SVG (no image round trip, follows the theme contrast with a white plate). */
export function QrCode({ value, size = 184, label }: { value: string; size?: number; label: string }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    const dark: Array<[number, number]> = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) dark.push([c, r]);
    return { n, dark };
  }, [value]);
  const m = 2; // quiet zone
  return (
    <svg className="qr" role="img" aria-label={label} width={size} height={size} viewBox={`0 0 ${cells.n + m * 2} ${cells.n + m * 2}`} shapeRendering="crispEdges">
      <rect width="100%" height="100%" fill="#fff" />
      {cells.dark.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x + m} y={y + m} width="1" height="1" fill="#000" />
      ))}
    </svg>
  );
}

/** Secret key in groups of 4 so it can be typed into the app by hand. */
const groupKey = (s: string) => s.replace(/(.{4})/g, '$1 ').trim();

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Recovery codes, shown once. The person confirms they kept them before continuing: losing the
 * phone without them means asking an administrator for help.
 */
export function RecoveryCodes({ codes, onDone, doneLabel = 'Continuar', large }: { codes: string[]; onDone: () => void; doneLabel?: string; large?: boolean }) {
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  const text = `Códigos de recuperación (cada uno sirve una sola vez)\n\n${codes.join('\n')}\n`;
  return (
    <div className="stack">
      <Notice tone="warn" title="Guarda estos códigos ahora">
        Si pierdes el teléfono, cada código te deja ingresar una vez. No los volverás a ver: cópialos o descárgalos y guárdalos fuera del teléfono.
      </Notice>
      <ol className="recovery-codes mono" aria-label="Códigos de recuperación">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <div className="row">
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            void navigator.clipboard?.writeText(codes.join('\n')).then(
              () => toast('Códigos copiados'),
              () => toast('No se pudo copiar; descárgalos', true),
            )
          }
        >
          <Copy size={14} aria-hidden /> Copiar
        </button>
        <button type="button" className="btn sm" onClick={() => download('codigos-de-recuperacion.txt', text)}>
          <Download size={14} aria-hidden /> Descargar
        </button>
      </div>
      <label className="check">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> Guardé los códigos en un lugar seguro
      </label>
      <button type="button" className={`btn primary block ${large ? 'lg' : ''}`} disabled={!saved} onClick={onDone}>
        {doneLabel}
      </button>
    </div>
  );
}

/** Adding the account to an authenticator app: QR on screens, link and key on the phone itself. */
export function AuthenticatorSetup({ secret, otpauthUrl }: MfaSetup) {
  return (
    <div className="stack">
      <ol className="steps small">
        <li>Instala una app de autenticación en tu teléfono (Google Authenticator, Microsoft Authenticator o similar).</li>
        <li>Escanea el código QR con la app. Si estás en el mismo teléfono, toca «Agregar a la app» o escribe la clave.</li>
        <li>Escribe abajo el código de 6 dígitos que muestra la app.</li>
      </ol>
      <div className="mfa-setup">
        <QrCode value={otpauthUrl} label="Código QR para agregar la cuenta a la app de autenticación" />
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <span className="small muted">Clave para escribir a mano</span>
          <code className="secret-key">{groupKey(secret)}</code>
          <a className="btn sm" href={otpauthUrl}>
            <Smartphone size={14} aria-hidden /> Agregar a la app
          </a>
        </div>
      </div>
    </div>
  );
}

/** Brand of the company on the screens without a session. */
export interface AuthBrand {
  brandName: string;
  logoUrl?: string | null;
}

/**
 * How the screens without a session are laid out: the centered card (default) or the panel's split
 * screen, with the brand and its drawing beside the card (DS-007). Set once by `SessionGate`.
 */
export type AuthLayout = 'card' | 'split';
export const AuthLayoutContext = createContext<AuthLayout>('card');

/** The brand side of the split screen: decoration only (the card's heading names the company). */
function AuthAside({ brand }: { brand: AuthBrand }) {
  return (
    <div className="auth-aside" aria-hidden>
      <AuthMotif />
      <div className="auth-aside-brand">
        {brand.logoUrl && <img src={brand.logoUrl} alt="" height={28} />}
        <span>{brand.brandName}</span>
      </div>
      {/* the six stages of a trip, the signature of the panel (StateRail) */}
      <div className="auth-aside-rail">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <span key={n} className={n <= 4 ? 'on' : undefined} />
        ))}
      </div>
    </div>
  );
}

/**
 * The centered card of every screen without a session (sign in, steps, status). Its heading takes
 * the focus when the screen changes, so screen readers announce where the person is.
 */
export function AuthCard({
  brand,
  title,
  children,
  error,
  onSubmit,
  label,
  done,
}: {
  brand: AuthBrand;
  title: ReactNode;
  children: ReactNode;
  error?: string | null;
  onSubmit?: (e: FormEvent) => void;
  /** Accessible name of the card; the brand by default. */
  label?: string;
  /** A step that ended well (password changed, account active): the split layout shows a seal. */
  done?: boolean;
}) {
  const layout = useContext(AuthLayoutContext);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  const body = (
    <>
      {done && layout === 'split' && (
        <span className="auth-seal" aria-hidden>
          <Check size={28} strokeWidth={2.5} />
        </span>
      )}
      <div className="auth-brand">
        {brand.logoUrl && <img src={brand.logoUrl} alt="" height={44} />}
        <h1 id="auth-title" ref={heading} tabIndex={-1}>
          {brand.brandName}
        </h1>
        <div className="muted">{title}</div>
      </div>
      {children}
      {error && (
        <div className="error-box" role="alert">
          {error}
        </div>
      )}
    </>
  );
  const card = onSubmit ? (
    <form className="card stack auth-card" aria-labelledby={label ? undefined : 'auth-title'} aria-label={label} onSubmit={onSubmit}>
      {body}
    </form>
  ) : (
    <section className="card stack auth-card" aria-labelledby={label ? undefined : 'auth-title'} aria-label={label}>
      {body}
    </section>
  );
  if (layout === 'split')
    return (
      <div className="auth auth-split">
        <AuthAside brand={brand} />
        <div className="auth-main">{card}</div>
      </div>
    );
  return <div className="auth">{card}</div>;
}

/** A wait between screens without a session, inside the same frame as the screens around it. */
export function AuthSpinner({ label, brand }: { label: string; brand?: AuthBrand }) {
  const layout = useContext(AuthLayoutContext);
  if (layout === 'split' && brand)
    return (
      <div className="auth auth-split">
        <AuthAside brand={brand} />
        <div className="auth-main">
          <Spinner label={label} />
        </div>
      </div>
    );
  return (
    <div className="auth">
      <Spinner label={label} />
    </div>
  );
}
